import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Star, Video, CheckCircle2, User, Linkedin } from "lucide-react";
import { toast } from "sonner";

// Generate initials from name
const getInitials = (name) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Currency symbols mapping
const getCurrencySymbol = (currency) => {
  const symbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    AED: 'AED ',
    INR: '₹',
    SAR: 'SAR ',
    QAR: 'QAR ',
    KWD: 'KWD ',
    BHD: 'BHD ',
    OMR: 'OMR '
  };
  return symbols[currency] || '$';
};

// Handle LinkedIn click
const handleLinkedInClick = (e, expert) => {
  e.preventDefault();
  e.stopPropagation();
  
  if (expert.stay_anonymous) {
    toast.info("This expert has chosen to remain anonymous");
    return;
  }
  
  if (expert.linkedin) {
    window.open(expert.linkedin, '_blank', 'noopener,noreferrer');
  }
};

export const ExpertCard = ({ expert }) => {
  const { t } = useTranslation();
  const initials = getInitials(expert.name || 'EX');
  const hasPhoto = expert.image_url && expert.image_url.length > 0 && !expert.stay_anonymous;
  const currencySymbol = getCurrencySymbol(expert.currency || 'USD');
  const showLinkedIn = !expert.stay_anonymous && expert.linkedin;
  // Ensure expertise is always an array
  const expertiseList = Array.isArray(expert.expertise) ? expert.expertise : [];
  
  return (
    <Link 
      to={`/experts/${expert.id}`}
      className="block group expert-card bg-charcoal border border-slate-800 hover:border-gold-500/50 rounded-sm overflow-hidden transition-all duration-500"
      data-testid={`expert-card-${expert.id}`}
    >
      {/* Avatar Section */}
      <div className="relative aspect-[16/10] sm:aspect-[4/3] overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
        {hasPhoto ? (
          <img 
            src={expert.image_url.startsWith('http') ? expert.image_url : `${process.env.REACT_APP_BACKEND_URL}${expert.image_url}`}
            alt={expert.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className={`w-16 h-16 sm:w-24 sm:h-24 bg-gold-500/20 rounded-full items-center justify-center transition-transform duration-500 group-hover:scale-110 ${hasPhoto ? 'hidden' : 'flex'}`}
        >
          <span className="font-display text-2xl sm:text-4xl text-gold-500">{initials}</span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-2 sm:top-4 left-2 sm:left-4 flex gap-2">
          {expert.verified && (
            <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs bg-gold-500/90 text-black px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-sm font-medium">
              <CheckCircle2 className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
              {t('experts.card.verified')}
            </span>
          )}
        </div>

        {/* Price Badge */}
        <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4">
          <span className="bg-charcoal/90 backdrop-blur-sm text-gold-500 font-semibold px-2 sm:px-3 py-1 sm:py-1.5 rounded-sm text-xs sm:text-sm">
            {currencySymbol}{expert.hourly_rate}{t('experts.card.perHour')}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-display text-lg sm:text-xl group-hover:text-gold-500 transition-colors truncate flex-1">
            {expert.name}
          </h3>
          {/* LinkedIn Icon */}
          {showLinkedIn && (
            <button
              onClick={(e) => handleLinkedInClick(e, expert)}
              className="flex-shrink-0 p-1.5 sm:p-2 bg-[#0A66C2]/20 hover:bg-[#0A66C2]/30 rounded-sm transition-colors"
              title="View LinkedIn Profile"
              data-testid={`linkedin-btn-${expert.id}`}
            >
              <Linkedin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0A66C2]" />
            </button>
          )}
        </div>
        <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4 line-clamp-2">
          {expert.title}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm mb-3 sm:mb-4">
          <div className="flex items-center gap-1">
            <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-gold-500 text-gold-500" />
            <span className="font-medium">{expert.rating}</span>
          </div>
          <span className="text-slate-600">|</span>
          <div className="flex items-center gap-1 text-slate-400">
            <Video className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{expert.total_sessions} {t('experts.card.sessions')}</span>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {expertiseList.slice(0, 2).map((exp, i) => (
            <span 
              key={i}
              className="px-2 py-0.5 sm:px-2.5 sm:py-1 bg-slate-800/50 text-[10px] sm:text-xs text-slate-400 rounded-full truncate max-w-[120px] sm:max-w-none"
            >
              {exp}
            </span>
          ))}
          {expertiseList.length > 2 && (
            <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 bg-slate-800/50 text-[10px] sm:text-xs text-slate-500 rounded-full">
              +{expertiseList.length - 2}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
